import { useState, useEffect } from "react";
import { Loader2, Play, FileText, Download, Video, ChevronRight, Maximize2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PresentationViewer() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedSub, setSelectedSub] = useState(null);
  const [showVideo, setShowVideo] = useState(false);
  const [showSlideFull, setShowSlideFull] = useState(false);

  useEffect(() => {
    const pathParts = window.location.pathname.split("/");
    const id = pathParts[pathParts.length - 1];
    if (!id) { setError("Presentation not found."); setLoading(false); return; }

    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/presentation/view/${id}`);
        setData(res.data);
      } catch (err) {
        setError("Presentation not found or unavailable.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-slate-400 animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <p className="text-slate-500 text-lg">{error || "Not found."}</p>
      </div>
    );
  }

  const subs = data.enriched_subcategories || [];
  const pdfUrl = `${API}/presentations/${data.id}/pdf`;

  // Group by category
  const grouped = {};
  subs.forEach((s) => {
    if (!grouped[s.category]) grouped[s.category] = [];
    grouped[s.category].push(s);
  });

  // Detail view for a selected product
  if (selectedSub) {
    const videoStreamUrl = selectedSub.video_id ? `${API}/resource/${selectedSub.video_id}/stream` : null;
    const videoDownloadUrl = selectedSub.video_id ? `${API}/resource/${selectedSub.video_id}/download` : null;
    const datasheetUrl = selectedSub.has_datasheet ? `${API}/presentation/datasheet/${selectedSub.id}/download` : null;
    const slideUrl = selectedSub.has_slide ? `${API}/presentation/slide/${selectedSub.id}` : null;

    // Slide-first layout: the product's single slide image is the main full-screen content
    if (slideUrl) {
      return (
        <div className="min-h-screen bg-slate-900 flex flex-col">
          <div className="flex items-center justify-between px-4 sm:px-6 py-3">
            <button onClick={() => { setSelectedSub(null); setShowVideo(false); setShowSlideFull(false); }} className="flex items-center gap-1 text-sm text-slate-300 hover:text-white" data-testid="slide-back-btn">
              <ChevronRight className="h-4 w-4 rotate-180" /> Back to overview
            </button>
            <div className="flex items-center gap-2">
              {videoStreamUrl && (
                <button onClick={() => setShowVideo(true)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 px-3 py-1.5 rounded-lg" data-testid="slide-video-btn">
                  <Play className="h-3.5 w-3.5" /> Watch Video
                </button>
              )}
              {datasheetUrl && (
                <a href={datasheetUrl} download className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 px-3 py-1.5 rounded-lg" data-testid="slide-datasheet-btn">
                  <FileText className="h-3.5 w-3.5" /> Datasheet
                </a>
              )}
              <button onClick={() => setShowSlideFull(true)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-900 bg-white hover:bg-slate-100 px-3 py-1.5 rounded-lg" data-testid="slide-fullscreen-btn">
                <Maximize2 className="h-3.5 w-3.5" /> Full screen
              </button>
            </div>
          </div>
          <div className="flex-1 flex items-center justify-center px-4 sm:px-6 pb-6">
            <img src={slideUrl} alt={selectedSub.title} onClick={() => setShowSlideFull(true)} className="max-w-full max-h-[calc(100vh-90px)] object-contain rounded-lg shadow-2xl cursor-zoom-in" data-testid="slide-image" />
          </div>

          {showSlideFull && (
            <div className="fixed inset-0 bg-black flex items-center justify-center z-50" onClick={() => setShowSlideFull(false)} data-testid="slide-fullscreen-overlay">
              <button onClick={() => setShowSlideFull(false)} className="absolute top-4 right-5 text-white/70 hover:text-white text-sm z-10">Close ✕</button>
              <img src={slideUrl} alt={selectedSub.title} className="max-w-full max-h-full object-contain" />
            </div>
          )}

          {showVideo && videoStreamUrl && (
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowVideo(false)}>
              <div className="w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-end mb-2">
                  <button onClick={() => setShowVideo(false)} className="text-white/70 hover:text-white text-sm">Close</button>
                </div>
                <div className="aspect-video bg-black rounded-xl overflow-hidden">
                  <video controls autoPlay playsInline className="w-full h-full">
                    <source src={videoStreamUrl} type="video/mp4" />
                  </video>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
          <button onClick={() => { setSelectedSub(null); setShowVideo(false); }} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-6">
            <ChevronRight className="h-4 w-4 rotate-180" /> Back to overview
          </button>

          {/* Video Modal */}
          {showVideo && videoStreamUrl && (
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowVideo(false)}>
              <div className="w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-end mb-2">
                  <button onClick={() => setShowVideo(false)} className="text-white/70 hover:text-white text-sm">Close</button>
                </div>
                <div className="aspect-video bg-black rounded-xl overflow-hidden">
                  <video controls autoPlay playsInline className="w-full h-full">
                    <source src={videoStreamUrl} type="video/mp4" />
                  </video>
                </div>
              </div>
            </div>
          )}

          {/* Hero */}
          <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl px-8 py-12 mb-6 text-white">
            <p className="text-white/70 text-xs font-semibold uppercase tracking-wider mb-2">{selectedSub.category}</p>
            <h1 className="text-3xl font-bold mb-1">{selectedSub.title}</h1>
            <p className="text-white/80">{selectedSub.tagline}</p>
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <h2 className="text-lg font-bold text-slate-900 mb-3">Overview</h2>
              <p className="text-sm text-slate-600 leading-relaxed">{selectedSub.description}</p>
            </div>
            <div className="space-y-4">
              {videoStreamUrl && (
                <button onClick={() => setShowVideo(true)} className="w-full bg-white rounded-xl border border-slate-200 shadow-sm p-5 hover:shadow-md transition-all text-left group">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center group-hover:bg-red-100 flex-shrink-0"><Play className="h-6 w-6 text-red-500 fill-red-500" /></div>
                    <div><p className="text-sm font-bold text-slate-900">Watch Video</p><p className="text-xs text-slate-500">Product demonstration</p></div>
                  </div>
                </button>
              )}
              {videoDownloadUrl && (
                <a href={videoDownloadUrl} download className="block">
                  <div className="w-full bg-white rounded-xl border border-slate-200 shadow-sm p-5 hover:shadow-md transition-all text-left group">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-purple-50 flex items-center justify-center group-hover:bg-purple-100 flex-shrink-0"><Video className="h-6 w-6 text-purple-500" /></div>
                      <div><p className="text-sm font-bold text-slate-900">Download Video</p><p className="text-xs text-slate-500">Save to device</p></div>
                    </div>
                  </div>
                </a>
              )}
              {datasheetUrl && (
                <a href={datasheetUrl} download className="block">
                  <div className="w-full bg-white rounded-xl border border-slate-200 shadow-sm p-5 hover:shadow-md transition-all text-left group">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 flex-shrink-0"><FileText className="h-6 w-6 text-blue-500" /></div>
                      <div><p className="text-sm font-bold text-slate-900">Download Datasheet</p><p className="text-xs text-slate-500">{selectedSub.datasheet_filename}</p></div>
                    </div>
                  </div>
                </a>
              )}
              {!videoStreamUrl && !datasheetUrl && (
                <div className="bg-slate-50 rounded-xl border border-slate-200 p-5 text-center">
                  <p className="text-sm text-slate-400">Resources coming soon</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Overview
  const preparedFor = data.customer_name ? `${data.customer_name}, ${data.customer_company}` : data.customer_company;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="text-center mb-10">
          <img src="/zkteco-logo.png" alt="ZKTeco" className="h-10 w-auto mx-auto mb-4" />
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">{data.title || "ZKTeco Product Portfolio"}</h1>
          <p className="text-base text-slate-500">Prepared for <strong>{preparedFor}</strong></p>
          <a href={pdfUrl} download className="inline-flex items-center gap-1.5 mt-4 text-sm text-blue-600 hover:text-blue-800 font-medium">
            <Download className="h-4 w-4" /> Download PDF
          </a>
        </div>

        {/* Products */}
        {Object.entries(grouped).map(([category, items]) => (
          <div key={category} className="mb-8">
            <h2 className="text-lg font-bold text-slate-800 mb-3">{category}</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((sub) => (
                <button
                  key={sub.id}
                  onClick={() => setSelectedSub(sub)}
                  className="group bg-white rounded-xl border border-slate-200 shadow-sm p-5 hover:shadow-md hover:border-slate-300 transition-all text-left"
                >
                  <h3 className="text-sm font-bold text-slate-900 mb-0.5">{sub.title}</h3>
                  <p className="text-xs text-slate-500 mb-2">{sub.tagline}</p>
                  <div className="flex items-center gap-2">
                    {sub.has_slide && <span className="text-[10px] font-semibold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">SLIDE</span>}
                    {sub.has_video && <span className="text-[10px] font-semibold text-red-500 bg-red-50 px-1.5 py-0.5 rounded">VIDEO</span>}
                    {sub.has_datasheet && <span className="text-[10px] font-semibold text-blue-500 bg-blue-50 px-1.5 py-0.5 rounded">DATASHEET</span>}
                  </div>
                  <div className="flex items-center gap-1 mt-2 text-xs font-semibold text-slate-400 group-hover:text-slate-700 transition-colors">
                    View Details <ChevronRight className="h-3 w-3" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}

        {/* Footer */}
        <div className="text-center mt-10 pt-6 border-t border-slate-200">
          <p className="text-sm text-slate-500">ZKTeco UK in partnership with {data.sender_company || "ZKTeco UK"}</p>
        </div>
      </div>
    </div>
  );
}
